// Game-phase lifecycle: attract -> waveIntro -> playing -> waveEnd -> (next wave)
// and -> gameOver. Wave generation, base refill, and city restoration happen on
// the relevant transitions.

import type { GameState } from '../state/types';
import type { ApplyPhaseTransitions } from './contracts';
import { generateWave } from '../waves/generateWave';
import { mapBases, mapCities } from '../state/update';
import { aliveTargets } from '../state/targets';
import { floatInRange } from '../rng/mulberry32';
import {
  AMMO_PER_BASE,
  FIXED_DT,
  FLYING_CHANCE,
  FLYING_INTRO_WAVE,
  WAVE_INTRO_T,
  WAVE_SPAWN_WINDOW,
  multiplierForWave,
} from '../constants';

/** Enter `playing` for the given wave: generate its spawn queue and (maybe) a
 *  flyer schedule, all deterministically from the RNG. */
function startWave(state: GameState, wave: number): GameState {
  const { queue, rng } = generateWave(wave, state.rng, aliveTargets(state));
  let s = rng;
  let flyingSpawnAt: number | null = null;
  if (wave >= FLYING_INTRO_WAVE) {
    const roll = floatInRange(s, 0, 1);
    s = roll.state;
    if (roll.value < FLYING_CHANCE) {
      const at = floatInRange(s, 1, WAVE_SPAWN_WINDOW);
      s = at.state;
      flyingSpawnAt = at.value;
    }
  }
  return {
    ...state,
    phase: { kind: 'playing' },
    wave,
    spawnQueue: queue,
    waveTimer: 0,
    flyingSpawnAt,
    rng: s,
  };
}

/** Reset to a fresh wave-1 game (keeps the RNG stream running). */
function freshGame(state: GameState): GameState {
  return {
    ...state,
    cities: mapCities(state.cities, (c) => ({ ...c, alive: true })),
    bases: mapBases(state.bases, (b) => ({ ...b, alive: true, ammo: AMMO_PER_BASE })),
    wave: 1,
    score: 0,
    scoreMultiplier: multiplierForWave(1),
    bonusCityProgress: 0,
    reserveCities: 0,
    enemyMissiles: [],
    playerMissiles: [],
    explosions: [],
    flyingObjects: [],
    spawnQueue: [],
    flyingSpawnAt: null,
    waveTimer: 0,
    pendingKills: [],
    phase: { kind: 'waveIntro', wave: 1, timer: WAVE_INTRO_T },
  };
}

/** Advance to the next wave: refill bases, restore destroyed cities from reserve. */
function advanceWave(state: GameState): GameState {
  const wave = state.wave + 1;
  let reserveCities = state.reserveCities;
  const bases = mapBases(state.bases, (b) =>
    b.alive ? { ...b, ammo: AMMO_PER_BASE } : b,
  );
  const cities = mapCities(state.cities, (c) => {
    if (!c.alive && reserveCities > 0) {
      reserveCities -= 1;
      return { ...c, alive: true };
    }
    return c;
  });
  return {
    ...state,
    wave,
    scoreMultiplier: multiplierForWave(wave),
    reserveCities,
    bases,
    cities,
    waveTimer: 0,
    phase: { kind: 'waveIntro', wave, timer: WAVE_INTRO_T },
  };
}

export const applyPhaseTransitions: ApplyPhaseTransitions = (state, input) => {
  const p = state.phase;
  switch (p.kind) {
    case 'attract':
      return input.start ? freshGame(state) : state;
    case 'waveIntro': {
      const timer = p.timer - FIXED_DT;
      return timer <= 0 ? startWave(state, p.wave) : { ...state, phase: { ...p, timer } };
    }
    case 'playing':
      return state;
    case 'waveEnd': {
      const timer = p.timer - FIXED_DT;
      return timer <= 0 ? advanceWave(state) : { ...state, phase: { ...p, timer } };
    }
    case 'gameOver': {
      if (input.start) return freshGame(state);
      const timer = p.timer - FIXED_DT;
      return timer <= 0
        ? { ...state, phase: { kind: 'attract' } }
        : { ...state, phase: { ...p, timer } };
    }
  }
};
